import { useState, useEffect, useContext } from 'react';
import {
    Container,
    Box,
    Grid,
    Paper,
    TableContainer,
    TableRow,
    TableHead,
    TableCell,
    Table,
    TableBody,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormSelect,
    SttList,
    SttFormText,
    SttTabs,
} from '../../../sporttia/all';
import ApiModelDialog from './ApiModelDialog';

export default function Apis({}) {
    // Methods
    const methods = ['GET', 'POST', 'PUT', 'DELETE'];

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    // const navigate = useNavigate();
    const [apis, setApis] = useState<any>();
    const [versions, setVersions] = useState<any[]>([]);
    const [filter, setFilter] = useState<any>({});
    const [selectedApi, setSelectedApi] = useState<any>();
    const [categories, setCategories] = useState<any[]>([]);
    const [models, setModels] = useState<any>([]);
    const [openModel, setOpenModel] = useState<{ id: number } | null>(null);

    /**
     * Load API's
     */
    const loadApis = () => {
        cxt.api('GET', '/apis', {
            params: {
                idVersion: filter.version.id,
                page: 1,
                rows: 1000,
            },
            success: (r: any) => {
                setApis(r);

                // Set first one
                selectApi(r.rows[0]);

                // Build category list
                const auxCats: string[] = [];
                r.rows.forEach((api: any) => {
                    if (!auxCats.includes(api.category)) {
                        auxCats.push(api.category);
                    }
                });
                const newCategories: { value: string; caption: string }[] = [];
                auxCats.forEach((cat: string) => {
                    newCategories.push({
                        value: cat,
                        caption: cat,
                    });
                });
                setCategories(newCategories as any);
            },
        });
    };

    /**
     * Load versions
     */
    const loadVersions = () => {
        cxt.api('GET', '/apis/versions', {
            success: (r: any) => {
                setVersions(r.rows);

                // Set actual version
                // setFilter({version: r.rows[0]})
                setFilter({
                    endpoint: '',
                    method: 'GET',
                    version: r.rows.find((e: any) => e.code === 'v6'),
                });
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({ name, value }: any) => {
        setFilter({ ...filter, [name]: value });
    };

    /**
     * Actual version changes, load apis
     */
    useEffect(() => {
        if (filter.version) {
            loadApis();

            // Load all models
            cxt.api('GET', '/apis/models', {
                params: { version: filter.version.id },
                success(r) {
                    setModels(r);
                },
            });
        }
    }, [filter.version]);

    /**
     * Init
     */
    useEffect(() => {
        cxt.setHeader({
            title: "API's",
        });

        // Load versions
        loadVersions();

        setFilter({
            version: null,
            endpoint: '',
            method: 'GET',
        });
    }, []);

    /**
     * On select version
     */
    const onSelectVersion = ({ value }: any) => {
        setFilter({
            version: versions.find((e: any) => e.id === value),
        });
    };

    /**
     * On open model
     */
    const onOpenModel = (model: any) => {
        setOpenModel(model);
    };

    /**
     * Select API
     */
    const selectApi = (api: any) => {
        cxt.api('GET', `/apis/${api.id}`, {
            success: (r: any) => {
                setSelectedApi(r.api);
            },
        });
    };

    // -----| Render |-----
    return (
        <Container>
            <h1>Sporttia API documentation</h1>

            <Box pt={2}>
                <Paper>
                    <Box p={4}>
                        {filter && (
                            <Grid container spacing={3}>
                                <SttFormSelect
                                    grid
                                    md={2}
                                    name="version"
                                    caption={t('api.version')}
                                    defVal={filter.version && filter.version.id}
                                    onChange={onSelectVersion}
                                    options={versions.map((version: any) => ({
                                        caption: version.code,
                                        value: version.id,
                                    }))}
                                />

                                <SttFormSelect
                                    grid
                                    cleanable
                                    md={4}
                                    name="category"
                                    caption={t('api.category')}
                                    defVal={filter.category}
                                    onChange={handleForm}
                                    options={categories}
                                />

                                <SttFormSelect
                                    grid
                                    md={2}
                                    name="method"
                                    caption={t('api.method')}
                                    defVal={filter.method}
                                    onChange={handleForm}
                                    options={methods.map((m: any) => ({
                                        caption: m,
                                        value: m,
                                    }))}
                                />

                                <SttFormText
                                    grid
                                    md={4}
                                    name="endpoint"
                                    caption="Endpoint"
                                    defVal={filter.endpoint}
                                    onChange={handleForm}
                                />
                            </Grid>
                        )}

                        {apis && (
                            <Grid container spacing={3}>
                                <Grid size={{ md: 4 }}>
                                    {apis && (
                                        <SttList
                                            title="API's"
                                            onSelect={selectApi}
                                            data={apis.rows
                                                .filter(
                                                    (api: any) =>
                                                        filter.method ===
                                                        api.method,
                                                )
                                                .filter(
                                                    (api: any) =>
                                                        api.endpoint.indexOf(
                                                            filter.endpoint,
                                                        ) !== -1,
                                                )
                                                .filter(
                                                    (api: any) =>
                                                        !filter.category ||
                                                        api.category ===
                                                            filter.category,
                                                )
                                                .map((api: any) => ({
                                                    caption: `${api.method} ${api.endpoint}`,
                                                    text: api.name,
                                                    objToSelect: api,
                                                    selected:
                                                        selectedApi &&
                                                        selectedApi.id ===
                                                            api.id,
                                                }))}
                                        />
                                    )}
                                </Grid>

                                {selectedApi && (
                                    <Grid size={{ md: 8 }}>
                                        <h2>{selectedApi.endpoint}</h2>
                                        <p>{selectedApi.description}</p>

                                        <SttTabs
                                            tabs={[
                                                {
                                                    caption:
                                                        t('api.parameters'),
                                                    component: (
                                                        <ApisParameters
                                                            selectedApi={
                                                                selectedApi
                                                            }
                                                            models={models}
                                                            onOpenModel={
                                                                onOpenModel
                                                            }
                                                        />
                                                    ),
                                                },
                                                {
                                                    caption: t('api.responses'),
                                                    component: (
                                                        <ApisResponses
                                                            selectedApi={
                                                                selectedApi
                                                            }
                                                            models={models}
                                                            onOpenModel={
                                                                onOpenModel
                                                            }
                                                        />
                                                    ),
                                                },
                                            ]}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Box>
                </Paper>
            </Box>

            <ApiModelDialog
                idModel={openModel ? openModel.id : null}
                onClose={() => setOpenModel(null)}
            />
        </Container>
    );
}

function ApisParameters({ selectedApi, models, onOpenModel }: any) {
    const { t } = useTranslation();

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('labels.name')}</TableCell>
                        <TableCell>{t('general.description')}</TableCell>
                        <TableCell>{t('general.type')}</TableCell>
                        <TableCell>{t('api.mandatory')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {selectedApi &&
                        selectedApi.parameters &&
                        selectedApi.parameters.rows.map((param: any) => (
                            <TableRow key={param.id}>
                                <TableCell align="center">
                                    <b>{param.name}</b>
                                </TableCell>
                                <TableCell align="left">
                                    {param.description}
                                </TableCell>
                                <TableCell align="center">
                                    <ApisModel
                                        modelName={param.type}
                                        models={models}
                                        onOpenModel={onOpenModel}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {param.madatory ? <DoneIcon /> : null}
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ApisResponses({ selectedApi, models, onOpenModel }: any) {
    const { t } = useTranslation();

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('labels.name')}</TableCell>
                        <TableCell>{t('general.description')}</TableCell>
                        <TableCell>{t('general.type')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {selectedApi &&
                        selectedApi.responses &&
                        selectedApi.responses.rows.map((resp: any) => (
                            <TableRow key={resp.id}>
                                <TableCell align="center">
                                    <b>{resp.name}</b>
                                </TableCell>
                                <TableCell align="left">
                                    {resp.description}
                                </TableCell>
                                <TableCell align="center">
                                    <ApisModel
                                        modelName={resp.type}
                                        models={models}
                                        onOpenModel={onOpenModel}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ApisModel({ modelName, models, onOpenModel }: any) {
    const [model, setModel] = useState<any>();

    useEffect(() => {
        if (modelName && models) {
            setModel(
                models.rows.find(
                    (m: any) => m.name === modelName.replace('[]', ''),
                ),
            );
        }
    }, [modelName, models]);

    return (
        <>
            {model ? (
                <a onClick={() => onOpenModel(model)}>{modelName}</a>
            ) : (
                modelName
            )}
        </>
    );
}
